import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { getSiteBuild, publishSite, startSiteRestyle, type Site, type SiteBuild } from '@/services/siteApi';

// "Keep the look of your old site, or a fresh design?" A move brings the words
// and pictures over on plain pages, with none of the old styling, so the next
// question is how it should look. Joe, Oct 25 2026: the designer should ask
// whether they want a refresh or to mirror the look and feel of the old site.
// Either way it lands on the private preview; nothing is live until Publish.

const INK = 'var(--hj-ink)';
const btnBase: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44, padding: '10px 18px', borderRadius: 12, fontFamily: 'inherit', fontWeight: 600, fontSize: 15, cursor: 'pointer', boxSizing: 'border-box', textDecoration: 'none' };
const primary: React.CSSProperties = { ...btnBase, background: INK, color: '#fff', border: 0 };
const secondary: React.CSSProperties = { ...btnBase, background: '#fff', color: INK, border: '1px solid var(--hj-hairline)' };
const off = (s: React.CSSProperties, o: boolean): React.CSSProperties => (o ? { ...s, opacity: 0.45, cursor: 'not-allowed' } : s);
const body: React.CSSProperties = { fontSize: 15, lineHeight: 1.5, color: 'inherit', margin: 0 };

const SiteRestyle: React.FC<{ site: Site; onSite: (s: Site) => void; oldUrl?: string | null; onDismiss?: () => void }> = ({ site, onSite, oldUrl, onDismiss }) => {
  const [build, setBuild] = useState<SiteBuild | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  const running = build?.status === 'running';

  const poll = (id: string) => {
    timer.current = window.setTimeout(async () => {
      try {
        const r = await getSiteBuild(site.id, id);
        setBuild(r.build);
        if (r.build.status === 'running') { poll(id); return; }
        if (r.build.status === 'done' && r.site) onSite(r.site);
      } catch (e) { setErr((e as Error).message); setBuild(null); }
    }, 4000);
  };
  const go = async (style: 'mirror' | 'refresh') => {
    setErr(null); setBuild(null); setPublished(false);
    try { const r = await startSiteRestyle(site.id, style, note.trim()); setBuild(r.build); poll(r.build.id); }
    catch (e) { setErr((e as Error).message); }
  };
  const publish = async () => {
    if (!build?.versionId) return;
    setPublishing(true); setErr(null);
    try { onSite((await publishSite(site.id, build.versionId)).site); setPublished(true); }
    catch (e) { setErr((e as Error).message); }
    setPublishing(false);
  };

  const done = build?.status === 'done';
  return (
    <div className="flex flex-col" style={{ background: done && !build?.held ? 'var(--hj-beige)' : 'var(--hj-yellow-tint)', color: done && !build?.held ? 'var(--hj-beige-ink)' : INK, border: done ? '1px solid transparent' : '1px solid var(--hj-yellow)', borderRadius: 12, padding: 16, gap: 10 }}>
      {!done ? (
        <>
          <p style={{ ...body, fontWeight: 700 }}>How should it look?</p>
          <p style={body}>Your words and pictures are in, on plain pages. We can style them to match {oldUrl ? oldUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : 'your old site'}, or give the site a fresh design. Either one goes to your private preview first.</p>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} disabled={running} maxLength={300} placeholder="Anything to keep in mind? (optional) e.g. darker, bigger headshot"
            style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid var(--hj-hairline)', fontSize: 14, fontFamily: 'inherit', background: '#fff', color: INK }} />
          <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
            <button type="button" disabled={running} onClick={() => go('mirror')} style={off(primary, running)}>Match my old site</button>
            <button type="button" disabled={running} onClick={() => go('refresh')} style={off(secondary, running)}>Give it a fresh design</button>
            {onDismiss && !running && <button type="button" onClick={onDismiss} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontSize: 14, color: INK, textDecoration: 'underline', fontFamily: 'inherit' }}>Keep it plain for now</button>}
          </div>
          {running && <p className="flex items-center" style={{ ...body, gap: 8, fontSize: 14 }}><Loader2 className="w-4 h-4 animate-spin" /> Restyling your site. This takes a minute or two; stay on this page.</p>}
          {build?.status === 'failed' && <p style={{ ...body, fontSize: 14, color: 'var(--hj-pink-ink)' }}>{build.error}</p>}
        </>
      ) : (
        <>
          <p style={{ ...body, fontWeight: 700 }}>{published ? 'Published.' : build?.held ? 'Restyled, and it’s getting a quick review first.' : 'Restyled. Look it over, then publish.'}</p>
          {build?.summary && <p style={body}>{build.summary}</p>}
          {!published && !build?.held && (
            <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
              {site.previewUrl && <a href={site.previewUrl} target="_blank" rel="noreferrer" style={secondary}>Open preview <ExternalLink size={16} aria-hidden="true" /></a>}
              <button type="button" disabled={publishing} onClick={publish} style={off(primary, publishing)}>{publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publish'}</button>
              <button type="button" onClick={() => setBuild(null)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontSize: 14, color: 'inherit', textDecoration: 'underline', fontFamily: 'inherit' }}>Try the other look</button>
            </div>
          )}
          <p style={{ ...body, fontSize: 13.5, opacity: 0.85 }}>After that, change anything in plain words with “Change my site”.</p>
        </>
      )}
      {err && <p style={{ ...body, fontSize: 14, color: 'var(--hj-pink-ink)' }}>{err}</p>}
    </div>
  );
};

export default SiteRestyle;
